import {FlowStateController} from "@nolimitcity/slot-game/bin/flowcontrol/FlowStateController";
import {FlowStatePresentation} from "@nolimitcity/slot-game/bin/flowcontrol/FlowStatePresentation";
import {BrickSnake2000} from "../BrickSnake2000";
import {BrickSnake2000Sound} from "../sound/BrickSnake2000Sound";
import {gsap} from "gsap";
import {BrickSnake2000Symbol} from "../reels/symbols/BrickSnake2000Symbol";
import {BrickSnake2000ScatterSymbol} from "../reels/symbols/ScatterSymbol";
import {APISetting} from "@nolimitcity/slot-launcher/bin/interfaces/APISettingsSystem";
import {LoopableList} from "../utils/LoopableList";

export class BrickSnake2000NormalResultPresentation implements FlowStatePresentation {

    private maxWinLandSounds:LoopableList<string>;

    constructor() {
        this.maxWinLandSounds = new LoopableList<string>([
            "maxWinNoWinLand",
            "maxWinNoWinLand2",
            "maxWinNoWinLand3",
            "maxWinNoWinLand4",
            "maxWinNoWinLand5",
        ]);
        this.maxWinLandSounds.setNoDuplicates();
    }

    public start(flowStateController:FlowStateController) {
        const tl:gsap.core.Timeline = gsap.timeline({
            onComplete: () => {
                flowStateController.setSkipCallback();
            }
        });
        tl.add(() => flowStateController.setSkipCallback(() => tl.seek("End", false)));

        const isFastSpins = BrickSnake2000.apiPlugin.settings.get(APISetting.FAST_SPIN) &&
                            !BrickSnake2000.model.isGodMode;
        const isLuckyDraw = BrickSnake2000.model.data.wasFeatureBuy && BrickSnake2000.model.buyFeatureName == "LUCKY_DRAW";
        const wasGodModePurchase = BrickSnake2000.model.isGodMode;
        const isMaxWinStop = BrickSnake2000.model.data.reels[0]
                                                      .slice(1,6)
                                                      .some((sym) => sym == "MW");
        const isGodModeDeluxeStop = BrickSnake2000.model.isGodModeDeluxe;
        const stopAnimation = isGodModeDeluxeStop
                              ? this.godModeDeluxeStop()
                              : isMaxWinStop
                              ? this.maxWinStop(flowStateController, wasGodModePurchase)
                              : isFastSpins
                              ? this.fastStop()
                              : this.normalStop(isLuckyDraw);

        const jurPadding = BrickSnake2000.gameFlowController.calculatePaddingDuration(stopAnimation.duration());
        tl.add(stopAnimation, `>${Math.max(jurPadding - 0.45, 0)}`);
        tl.addLabel("End");

        tl.add(() => {
            flowStateController.finish()
        }, '>0.5');
    }

    private playSymbolLandingSounds(symbols:BrickSnake2000Symbol[]):gsap.Callback[] {
        return (symbols.map((sym) => {
            let landingSound = "";
            switch(sym.symbolName) {
                case "W":
                    landingSound = "wildLand";
                    break;
                case "XW":
                    landingSound = "xWaysLand";
                    break;
                case "S":
                    const isFirstLandOnXBetReel = sym.reelID == 1 && BrickSnake2000.model.isBoostedBet();
                    landingSound = isFirstLandOnXBetReel ? "" : `scatter${BrickSnake2000ScatterSymbol.scatterCount}`;
                    break;
            }
            return landingSound == "" ? () => {} : () => {BrickSnake2000.soundPlayer.playSound(landingSound)};
        }))
    }

    private fastStop():gsap.core.Timeline {
        return gsap.timeline().add(BrickSnake2000.baseGameReelView.reelModels.map((reel, index) => {
            return gsap.timeline()
                .add(reel.stop(BrickSnake2000.model.data.reels[index]))
                .add(() => {
                    BrickSnake2000.soundPlayer.playSound(index == 0
                                                         ? BrickSnake2000Sound.REEL_LANDING1
                                                         : BrickSnake2000Sound.REEL_LANDING2);
                })
                .add(() => {
                    if (index == 4)
                        BrickSnake2000.soundPlayer.stopSound(BrickSnake2000Sound.REEL_SPINNING)
                })
                .add(this.playSymbolLandingSounds(reel.getAllVisibleSymbols()))
                .add(reel.getAllVisibleSymbols().map((sym) => sym.getLandAnimation()), '<');
        }))
    }

    private godModeDeluxeStop():gsap.core.Timeline {
        return gsap.timeline().add(BrickSnake2000.baseGameReelView.reelModels.map((reel, index) => {
            return gsap.timeline()
                .add(reel.stop(BrickSnake2000.model.data.reels[index]))
                .add(() => {
                    BrickSnake2000.soundPlayer.playSound(index == 0
                        ? BrickSnake2000Sound.REEL_LANDING1
                        : BrickSnake2000Sound.REEL_LANDING2);
                })
                .add(() => {
                    if (index == 4)
                        BrickSnake2000.soundPlayer.stopSound(BrickSnake2000Sound.REEL_SPINNING)
                })
                .add(this.playSymbolLandingSounds(reel.getAllVisibleSymbols()))
                .add(reel.getAllVisibleSymbols().map((sym) => sym.getLandAnimation()), '<')
                .add(() => {BrickSnake2000.soundPlayer.playSound("maxWinWinLand")}, '<');
        }))
    }

    private normalStop(wasLuckyDraw:boolean):gsap.core.Timeline {
        const landedScatters:BrickSnake2000ScatterSymbol[] = [];
        const shouldPlayAnticipation = !BrickSnake2000.model.gameData.wasFeatureBuy ||
                                       BrickSnake2000.model.buyFeatureName == "LUCKY_DRAW";
        const antiStartCount = wasLuckyDraw ? 3 : 2;

        const tl = gsap.timeline();
        BrickSnake2000.baseGameReelView.reelModels
            .forEach((reel, index) => {
                const reelStopTl = gsap.timeline();
                const scatterCount = landedScatters.length;
                const stopDelay = shouldPlayAnticipation
                                  ? scatterCount == 4
                                    ? '<2.24'
                                    : scatterCount == 3
                                    ? '<1.68'
                                    : scatterCount == antiStartCount
                                    ? '<1.12'
                                    : '<0.28'
                                  : '<0.28';

                // stop reels
                reelStopTl.add(reel.stop(BrickSnake2000.model.data.reels[index]));
                reelStopTl.add(() => {
                    BrickSnake2000.soundPlayer.playSound(index == 0
                                                     ? BrickSnake2000Sound.REEL_LANDING1
                                                     : scatterCount >= antiStartCount
                                                       ? "anticipationReelStop"
                                                       : BrickSnake2000Sound.REEL_LANDING2)
                })
                reelStopTl.add(() => {
                    if (index == 4) {
                        BrickSnake2000.soundPlayer.stopSound(BrickSnake2000Sound.REEL_SPINNING)
                        BrickSnake2000.soundPlayer.stopSound("anticipation");
                        if (scatterCount >= antiStartCount)
                            BrickSnake2000.soundPlayer.playSound("anticipationFail");
                    }
                })
                reelStopTl.add(this.playSymbolLandingSounds(reel.getAllVisibleSymbols()));
                reelStopTl.add(reel.getAllVisibleSymbols().map((sym) => sym.getLandAnimation()), '<')

                // scatter anticipation
                const newScatter = <BrickSnake2000ScatterSymbol>(reel.getAllVisibleSymbols()
                                                                 .find((sym) => sym.symbolName == "S"));
                if (newScatter != undefined)
                    landedScatters.push(newScatter);
                if (index != 4 && landedScatters.length >= antiStartCount && shouldPlayAnticipation && newScatter != undefined) {
                    if (landedScatters.length == antiStartCount)
                        reelStopTl.add(() => {
                            BrickSnake2000.soundPlayer.playSound("anticipation");
                        }, '<')
                    reelStopTl.add(landedScatters.length == antiStartCount
                                   ? landedScatters.map((sym) => sym.startAnticipation())
                                   : reelStopTl.add(newScatter.startAnticipation()))
                }
                if (index == 4) {
                    reelStopTl.add(landedScatters.map((sym) => (<BrickSnake2000ScatterSymbol>sym).stopAnticipation()));
                }

                tl.add(reelStopTl, stopDelay);
            });
        return tl;
    }

    private maxWinStop(flowStateController:FlowStateController, wasGodMode:boolean):gsap.core.Timeline {

        const tl = gsap.timeline();
        const firstReelIndex = BrickSnake2000.model.data.reels[0].findIndex((sym) => sym == "MW");
        const landedMaxWin = BrickSnake2000.model.data.reels[4][firstReelIndex] == "MW";

        if (wasGodMode) {
            tl.add(() => {
                flowStateController.setSkipCallback();
            })
        }
        BrickSnake2000.baseGameReelView.reelModels
            .forEach((reel, index) => {
                const reelStopTl = gsap.timeline();
                const stopDelay = index == 4 ? '<2.8' : '<0.28';

                // stop reels
                reelStopTl.add(reel.stop(BrickSnake2000.model.data.reels[index]));
                reelStopTl.add(() => {
                    if (index == 0) {
                        BrickSnake2000.soundPlayer.playSound("maxWinMaxLand");
                        BrickSnake2000.soundPlayer.playSound("maxWinAnticipation");
                    }
                    if (index == 4) {
                        BrickSnake2000.soundPlayer.playSound(landedMaxWin
                                                             ? "maxWinWinLand"
                                                             : this.maxWinLandSounds.next());
                        BrickSnake2000.soundPlayer.stopSound("maxWinAnticipation");
                        BrickSnake2000.soundPlayer.stopSound(BrickSnake2000Sound.REEL_SPINNING)
                    }
                })
                reelStopTl.add(this.playSymbolLandingSounds(reel.getAllVisibleSymbols()));
                reelStopTl.add(reel.getAllVisibleSymbols().map((sym) => sym.getLandAnimation()), '<')
                if (index == 0)
                    reelStopTl.add(BrickSnake2000.baseGameReelView.startMaxWinAnticipation(firstReelIndex), '<');
                tl.add(reelStopTl, stopDelay);
            });
        tl.add(BrickSnake2000.baseGameReelView.stopMaxWinAnticipation(), '<0.37');
        if (!landedMaxWin) {
            tl.add(() => {}, '>0.5');
        }
        return tl;
    }
}